import React, { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@gumtree/ui-library';
import MapModal from '@gumtree/ui-library/src/maps/MapModal';
import { OverviewWrapper, OverviewTitle, OverviewList, OverviewListItem, InfoTipsWrapper, MapText, VipInfoTips } from './Overview.style';
import { VipState } from '../../reducers';

export interface OverviewItem {
    firstIconType?: string;
    secondIconType?: string;
    label?: string;
    value?: string;
    link?: {
        text: string;
        href?: string;
        target?: string;
        action?: string;
    };
}

export interface OverviewProps {
    eleId?: string;
    title?: string;
    items: OverviewItem[];
    className?: string;
}

// keep code for next, hide this for temp as requirement.
const TipTrigger = false;

const renderIconByType = (props: { iconType?: string; className?: string }) => {
    if (!props.iconType) return null;
    return <Icon className={props.className} type={props.iconType} />;
};

const renderGBGIcon = (setIsGbgOpen: (isOpen: boolean) => void, isGbgOpen: boolean) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    
    const debouncedSetOpen = useCallback((isOpen: boolean) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            setIsGbgOpen(isOpen);
        }, 300);
    }, [setIsGbgOpen]);

    return <InfoTipsWrapper>
        <Icon type="info" onClick={
            () => setIsGbgOpen(!isGbgOpen)
        }
            // @ts-ignore
            onMouseEnter={
                () => setIsGbgOpen(true)
            }
            onMouseLeave={
                () => debouncedSetOpen(false)
            }
        />
        {isGbgOpen && <VipInfoTips>Verified business</VipInfoTips>}
    </InfoTipsWrapper>
};

const Overview: React.FC<OverviewProps> = ({ items, title, className, eleId }) => {
    const {
        mapDetails: { latitude, longitude },
    } = useSelector((state: VipState) => state);
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const [isGbgOpen, setIsGbgOpen] = useState(false);
    const handleAction = (action?: string) => {
        if (action === 'openMap') {
            setIsMapModalOpen(true);
        }    
        // Handle other actions if any
    };

    const closeMapModal = () => {
        setIsMapModalOpen(false);
    };


    return (
        <OverviewWrapper id={eleId} className={className}>
            {title && <OverviewTitle>{title}</OverviewTitle>}
            <OverviewList>
                {items.map((item) => (
                    <OverviewListItem key={item.label}>
                        {renderIconByType({iconType: item.firstIconType, className: 'first-icon'})}
                        <span className="label">{item.label}</span>
                        {item.value && <span className="value">{item.value}</span>}
                        {item?.link?.href && (
                            <a href={item.link.href} target={item.link.target ?? '_blank'} rel="noopener noreferrer">
                                {item.link.text}
                            </a>
                        )}
                        {item?.link?.action && (
                            <MapText onClick={() => handleAction(item?.link?.action)} >{item.link.text}</MapText>
                        )}
                        {renderIconByType({iconType: item.secondIconType, className: 'second-icon'})}
                        {TipTrigger && item.firstIconType === 'gbg' && renderGBGIcon(setIsGbgOpen, isGbgOpen)}
                    </OverviewListItem>
                ))}
            </OverviewList>

            <MapModal
                isOpen={isMapModalOpen}
                onClose={closeMapModal}
                lat={latitude}
                long={longitude}
                dynamicMapApiKey={process.env.VIP_DYNAMIC_MAP_API_KEY ?? ''}
            />
        </OverviewWrapper>
    );
};

export default Overview;
